<template>
    <Card ref="nodeRef" :class="{'border-b': !last}" class="graphNodeCard">
        <template #title>
            <div class="flex items-center justify-between cursor-pointer" @click="openGraph">
                <div class="flex items-center gap-2">
                    <Checkbox v-if="selectionEnabled && (canEdit || canDelete)"
                              v-model="selectedElements"
                              :value="node"
                              data-cy="graph-select-checkbox"
                              @click.capture.stop></Checkbox>
                    <i
                        v-if="reorderEnabled"
                        class="pi pi-bars text-gray-500 graph-drag-handle"
                        title="Drag to reorder"
                        data-cy="graph-drag-handle"
                        @mousedown.stop="emit('drag-handle-down', node.id)"
                        @click.stop
                    />
                    <i class="pi pi-sitemap text-gray-600"/>
                    <span v-tooltip="node.name"
                        data-cy="graph-name"
                        class="font-medium" :class="{'truncate max-w-72': node.result}">{{ node.name }}</span>
                    <i
                        v-if="node.is_running"
                        class="pi pi-spin pi-spinner running-job-indicator"
                        v-tooltip="'PAC job is running in this graph'"
                    />
                </div>

                <div class="flex items-center gap-3" v-if="!isTrainee">
                    <div v-for="(v, t) in _.pick(node.result,[
                    'digitalization',
                    'adherence_ratio',
                    'coverage'
                ])" v-if="node.result" class="flex flex-col items-center" v-tooltip="progressTooltip(t, v)">
                        <span class="text-surface-900 text-base">{{ _.startCase(t) }}</span>
                        <ProgressBar :progress="v"
                                     :color="progressColor(v)"
                                     />
                    </div>
                    <div>
                        <Button type="button" icon="pi pi-ellipsis-v" text rounded
                                data-cy="graph-context-menu-button"
                                @click.prevent.stop="evt => ctxToggle(evt, node)"/>
                    </div>
                </div>

                <Menu ref="menu" :popup="true" :model="menuItems" data-cy="graph-context-menu">
                    <template #item="{ item, props }">
                        <a v-bind="props.action" :data-cy="item.dataCy">
                            <span v-if="item.icon" :class="item.icon"/>
                            <span class="ml-2">{{ item.label }}</span>
                        </a>
                    </template>
                </Menu>
            </div>
        </template>

        <template #content>
            <GraphPermission
                :key="'graphPerm' + node.id"
                v-if="selectedMenu === node && (
                    userCan(GRAPH_PERMISSION.MANAGE_PERMISSIONS, node) ||
                    userCan(WORKSPACE_PERMISSION.MANAGE_GRAPHS_PERMISSIONS, node.parent))"
                v-model:visible="showGraphPermissions"
                :graph="node"/>

            <DuplicateDialog
                :graph="node"
                v-if="selectedMenu === node && displayDuplicateDialog"
                v-model:visible="displayDuplicateDialog"
                @created="displayDuplicateDialog = false"/>

            <MoveToDialog
                :graphs="[node]"
                v-if="selectedMenu === node && displayMoveToDialog"
                v-model:visible="displayMoveToDialog"
                @moved="displayMoveToDialog = false"/>

            <Dialog header="Edit Graph name" v-model:visible="showEditName"
                    :style="{ width: '500px' }" :modal="true" @keyup.enter="updateName">
                <div class="confirmation-content">
                    <h3>Graph Name</h3>
                    <InputText required class="w-full" id="name" v-model="graphName" autofocus></InputText>
                </div>

                <template #footer>
                    <Button rounded label="Cancel" icon="pi pi-times" @click="showEditName=false"
                            class="p-button-text"/>
                    <Button rounded label="Confirm" icon="pi pi-check" @click.once="updateName"
                            :disabled="updateLoading"/>
                </template>
            </Dialog>
        </template>
    </Card>
</template>

<script setup>
import {ref, computed, defineAsyncComponent, watch} from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import GraphModel from "@/src/model/GraphModel";
import {GRAPH_PERMISSION, WORKSPACE_PERMISSION} from "@/src/model/Permission";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {progressTooltip, progressColor} from "@/src/utils/utils";

const GraphPermission = defineAsyncComponent(() => import('../graph/GraphPermission'))
const DuplicateDialog = defineAsyncComponent(() => import('../graph/DuplicateDialog'))
const MoveToDialog = defineAsyncComponent(() => import('../graph/MoveToDialog'));

import {useStore} from 'vuex';
import _ from "lodash-es";
import {OP, usePending} from "@/src/queries/pending";

const store = useStore();

const props = defineProps({
    node: GraphModel,
    depth: Number,
    depthColor: String,
    last: {
        type: Boolean,
        default: false,
    },
    selectionEnabled: {
        type: Boolean,
        default: false,
    },
    reorderEnabled: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits(['remove', 'update', 'drag-handle-down']);

const nodeRef = ref(null);

const selectedMenu = computed({
    get() {
        return store.getters["repoStore/selectedMenu"]
    },
    set(value) {
        store.commit('repoStore/setSelectedMenu', value)
    }
})
const menu = ref()

const ctxToggle = (evt, graph) => {
    menu.value.toggle(evt)
    selectedMenu.value = graph;
}

watch(selectedMenu, m => {
    if (m !== props.node && menu.value && menu.value.visible) {
        menu.value.hide()
    }
})

const userCan = store.getters['userStore/userCan']
const showGraphPermissions = ref(false)
const showEditName = ref(false);
const graphName = ref('')
const displayDuplicateDialog = ref(false)
const displayMoveToDialog = ref(false)

const isLocked = computed(() => store.getters["graphStore/isLockedByAnotherUser"](props.node))
const updateLoading = usePending(OP.UPDATE_GRAPH)

const updateName = () => {
    store.dispatch('graphStore/updateGraphFields', {data: {name: graphName.value}, graph: props.node})
    showEditName.value = false
    store.dispatch('repoStore/loadWorkspaces')
}

const canEdit = computed(() => {
    const graph = props.node
    return !isLocked.value && (userCan(GRAPH_PERMISSION.EDIT_GRAPH, graph) ||
        userCan(WORKSPACE_PERMISSION.EDIT_ALL_GRAPHS, graph.parent))
})

const canDelete = computed(() => {
    const graph = props.node
    return !isLocked.value && (userCan(GRAPH_PERMISSION.DELETE_GRAPH, graph) ||
        userCan(WORKSPACE_PERMISSION.DELETE_ALL_GRAPHS, graph.parent))
})

const menuItems = computed(() => {
    const graph = props.node
    let res = [
        {
            label: 'Manage permissions',
            icon: 'pi pi-fw pi-users',
            dataCy: 'graph-context-menu-item-manage-permissions',
            command: () => {
                showGraphPermissions.value = true
            },
            visible: !isLocked.value && (
                userCan(GRAPH_PERMISSION.MANAGE_PERMISSIONS, graph) ||
                userCan(WORKSPACE_PERMISSION.MANAGE_GRAPHS_PERMISSIONS, graph.parent)
            )
        },
        {
            label: 'Edit name',
            icon: 'pi pi-fw pi-pencil',
            dataCy: 'graph-context-menu-item-edit-name',
            command: () => {
                graphName.value = graph.name
                showEditName.value = true
            },
            visible: canEdit.value
        },
        {
            label: 'Move to',
            icon: 'pi pi-fw pi-folder',
            dataCy: 'graph-context-menu-item-move-to',
            command: () => {
                displayMoveToDialog.value = true
            },
            visible: canEdit.value
        },
        {
            label: 'Duplicate graph',
            icon: 'pi pi-fw pi-copy',
            dataCy: 'graph-context-menu-item-duplicate-graph',
            command: () => {
                displayDuplicateDialog.value = true
            },
            visible: true
        },
        {
            label: 'Delete',
            icon: 'pi pi-fw pi-trash',
            dataCy: 'graph-context-menu-item-delete',
            command: deleteItem,
            visible: canDelete.value
        },
    ].filter(item => item.visible)

    if (res.length > 0) {
        return res
    }

    return [
        {
            label: 'This graph has only view permission',
            dataCy: 'graph-context-menu-item-view-only',
            disabled: true
        }
    ]
})

const confirm = useConfirm();
const toast = useToast();
const deleteItem = (event) => {
    confirm.require({
        group: 'deleteWorkspace',
        target: event.target,
        message: `Are you sure you want to delete this graph? Also results will be deleted.`,
        icon: 'pi pi-exclamation-triangle',
        acceptProps: {rounded: true},
        acceptIcon: 'pi pi-check',
        rejectProps: {rounded: true},
        rejectIcon: 'pi pi-times',
        rejectClass: 'p-button-text',
        accept: async () => {
            //callback to execute when user confirms the action
            try {
                await store.dispatch('repoStore/deleteGraph', props.node.id)
            } catch (e) {
                toast.add({
                    severity: 'error',
                    summary: 'Delete failed',
                    detail: e?.response?.data?.error?.message || 'Could not delete this graph.',
                    life: 5000
                })
            }
        },
    });
}

const selectedElements = defineModel('selected')

const openGraph = async () => {
    if (!props.selectionEnabled) {
        await store.dispatch('repoStore/setSelectedGraph', props.node)
        return
    }
    if (selectedElements.value.includes(props.node)) {
        selectedElements.value = selectedElements.value.filter(n => n !== props.node)
    } else {
        selectedElements.value.push(props.node)
    }
}

const isTrainee = computed(() => store.getters['userStore/isTrainee'])

</script>
<style scoped>
.graphNodeCard {
    border-radius: 0.5rem;
    box-shadow: var(--shadow-1);
}

.graph-drag-handle {
    cursor: grab;
}

.running-job-indicator {
    color: #16a34a;
    font-size: 0.8rem;
}
</style>
